import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticFormModal, InfiniteScroll, ListFilter, ListRow, NoResults, Search, Switch, Tag, WithSelectable, WithTooltip } from 'basic-components';
import { Exit } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { DataScienceReadableConsts } from 'dashboard-services/api/dataScience';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DropdownService, Fetching } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { pipelinesQueries } from 'queries/modules/datascience';
import { sendEvent } from 'services/GAService';

import { useQueryClient } from '@tanstack/react-query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import FilterCarousel from 'primary/FilterCarousel';
import classNames from 'classnames';
import '../../Modal.scss';
import './Modal.scss';

const EMPTY_ARRAY = [];
const TRIGGER_TYPE_DELIMITER = encodeURIComponent("&");
const TRIGGER_TYPE_OPTIONS = DataScienceReadableConsts.PIPELINE_TRIGGER_TYPES
const Modal = ({ optionalText = undefined, closeModal, onSubmit, defaultValue = "", withVersion = false, submitText = "Select", triggerType = undefined, withRootId = false }) => {
  const dispatch = useDispatch(),
        favoritePipelines = useSelector(state => state.myAccountState.settingsState.favoritePipelines),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        [value, setValue] = useState(defaultValue),
        [query, setQuery] = useState(""),
        [showVersions, setShowVersions] = useState(false),
        [groupName, setGroupName] = useState(undefined),
        [tags, setTags] = useState(EMPTY_ARRAY),
        [tagQuery, setTagQuery] = useState(),
        [selectedTriggerTypes, setSelectedTriggerTypes] = useState(() => triggerType ? String(triggerType).split(TRIGGER_TYPE_DELIMITER) : EMPTY_ARRAY),
        activeFilterCount = Number(Boolean(groupName)) + tags.length + (!triggerType ? selectedTriggerTypes.length : 0),
        clearAllFilters = useCallback(() => {
          setGroupName(undefined)
          setTags(EMPTY_ARRAY)
          if(!triggerType) {
            setSelectedTriggerTypes(EMPTY_ARRAY)
          }
        }, [triggerType]),
        onSubmitLocal = useCallback(() => {
          onSubmit(value, true)
          closeModal()
        }, [closeModal, onSubmit, value]),
        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "" || value === " ",
          noDebounce: true
        }), [value]),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        queryCalculated = useMemo(() => {
          const localQuery = []
          if(query) {
            localQuery.push(String(query).includes("=") ? query : `*${query}*`)
          }
          if(groupName !== "" && groupName !== undefined) {
            localQuery.push(`groupName.keyword=${groupName}`)
          }
          tags.forEach(tag => {
            if(tag) {
              localQuery.push(`fields.tags=*${tag}*`)
            }
          })
          return localQuery.length === 0 ? "*" : localQuery.join("&")
        }, [groupName, query, tags]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, hasNextPage: hasNextPageGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = [] } = DropdownService.useQueryGroups({
          getApiConfig: getApiConfigLocal,
          input: groupQuery,
          queryClient
        }),
        { data: tagOptions = [], isFetching: isFetchingTags } = pipelinesQueries.usePipelineTags({
          getQueryBuilder,
          query: tagQuery
        }),
        triggerTypeOptions = useMemo(() => TRIGGER_TYPE_OPTIONS.filter(({ value }) => selectedTriggerTypes.includes(value)), [selectedTriggerTypes]),
        triggerTypeQuery = triggerType || selectedTriggerTypes.join(TRIGGER_TYPE_DELIMITER) || undefined,
        { data: pipelinesRaw, isPending, hasNextPage, fetchNextPage: onScroll, isFetchingNextPage } = pipelinesQueries.useSearchPipelines({
          query: queryCalculated,
          getQueryBuilder,
          size: 50,
          latest: showVersions ? null : true,
          triggerType: triggerTypeQuery,
          shouldRefetch: false
        }),
        pipelinesFromApi = useMemo(() => (pipelinesRaw?.pages || []).flatMap(d => d?.items || []), [pipelinesRaw]),
        pipelines = useMemo(() => !groupName && !query && tags.length === 0 && !triggerTypeQuery && !withRootId ? (favoritePipelines.map(d => Object.assign({}, d, { favorite: true }))).concat(pipelinesFromApi) : pipelinesFromApi, [favoritePipelines, groupName, pipelinesFromApi, query, tags.length, triggerTypeQuery, withRootId]),
        addTagToFilter = useCallback(tag => setTags(currentTags => currentTags.includes(tag) ? currentTags : [...currentTags, tag]), []),
        getUrl = useCallback(pipeline => DashboardLinkFactory.dataScience.pipeline({ groupName: pipeline.groupName, pipelineName: pipeline.name }), []),
        preventFilterSubmit = useCallback(event => event.preventDefault(), []),
        scrollContRef = useRef()

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "pipeline_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className={classNames(
          "ng-common-picker__modal",
          "ng-pipeline-picker__modal",
          { "ng-pipeline-picker__modal--with-version": withVersion },
        )}
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmitLocal}
        optionalText={optionalText}
        submitProps={submitProps}
        submitText={submitText}
        title="Choose a pipeline"
    >  
      <div className="ng-file-picker__modal__top">
        <div className="ng-file-picker__modal__top__search">
          <Search
              data-testid="pipeline-search"
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            activeFilterCount={activeFilterCount}
            className="ng-file-picker__modal__filters"
            onClearAll={clearAllFilters}
            onClickCapture={preventFilterSubmit}
        >
          <ListFilter
              defaultValue={groupName}
              fetchNextPage={fetchMoreGroups}
              hasNextPage={hasNextPageGroups}
              isFetching={isFetchingGroups || isFetchingNextPageGroups}
              label="Group"
              name="groupName"
              onChange={setGroupName}
              onInputChange={setGroupQuery}
              options={groups}
          />
          <ListFilter
              defaultValue={tags}
              isFetching={isFetchingTags}
              label="Tags"
              multi
              name="tags"
              onChange={setTags}
              onInputChange={setTagQuery}
              options={tagOptions}
          />
          <ListFilter
              defaultValue={triggerTypeOptions}
              disabled={!!triggerType}
              label="Trigger"
              multi
              name="triggerType"
              onChange={setSelectedTriggerTypes}
              options={TRIGGER_TYPE_OPTIONS}
          />
        </FilterCarousel>
      </div>
      <Fetching
          isFetching={isPending || isFetchingNextPage}
      />
      <When condition={withVersion}>
        {() => (
          <div className="ng-common-picker__modal__version-swap">
            <div className="ng-common-picker__modal__version-swap__label">
              Pick version
            </div>
            <Switch
                on={showVersions}
                onChange={setShowVersions}
            />
          </div>
        )}
      </When>
      <div className="ng-common-picker__modal__headers">
        <div className="ng-common-picker__modal__headers__cell">
          Name
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Group
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Tags
        </div>
        <When condition={withVersion}>
          {() => (
            <div className="ng-common-picker__modal__headers__cell">
              Version
            </div>
          )}
        </When>
        <div className="ng-common-picker__modal__headers__cell">
          Last modified
        </div>
        <div className="ng-common-picker__modal__headers__cell"/>
      </div>
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={onScroll}
            threshold={800}
            useWindow={false}
        >
          {pipelines.map((pipeline, index) => (
            <WithSelectable
                index={index}
                key={`Pipeline${index}`}
                selected={value?.name === pipeline.name && pipeline.groupName === value?.groupName && (!withVersion || pipeline.id === value.id)}
                setSelected={() => setValue(pipeline)}
            >
              <ListRow
                  className="ng-common-picker__modal__content__scroll__row"
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(pipeline)
                      onSubmitLocal()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(pipeline)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {pipeline.name}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {pipeline.groupName}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell ng-pipeline-picker__modal__tag-cell">
                  {[...(pipeline.tags || EMPTY_ARRAY)].sort((a, b) => a?.localeCompare(b)).map(tag => (
                    <Tag
                        isEditable={false}
                        key={tag}
                        onClick={event => {
                          event.preventDefault()
                          event.stopPropagation()
                          addTagToFilter(tag)
                        }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
                <When condition={withVersion}>
                  {() => (
                    <div className="ng-common-picker__modal__content__scroll__row__cell">
                      {pipeline.version}
                    </div>
                  )}
                </When>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {pipeline.lastChange ? TimeService.getUserFormattedDate(pipeline.lastChange, definedTime) : ""}
                </div>
                <div className="ng-common-picker__modal__exit">
                  <WithTooltip
                      data-place="top"
                      short
                      text="Open pipeline"
                  >
                    <div onClick={() => CommonService.openInNewTab(getUrl(pipeline))}>
                      <Exit
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                </div>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <When condition={pipelines.length === 0 && !isPending}>
          {() => (
            <NoResults
                isModal
            />
          )}
        </When>
      </div>
    </ElasticFormModal>
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  submitText: PropTypes.string,
  triggerType: PropTypes.string,
  withRootId: PropTypes.bool,
  withVersion: PropTypes.bool,
}

export default memo(Modal);
